import { useCallback, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'primary';
}

export function ConfirmDialog({
  open,
  options,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  options: ConfirmOptions | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!options) return null;

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={options.title}
      size="sm"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onCancel}>
            {options.cancelLabel ?? 'Cancel'}
          </Button>
          <Button
            type="button"
            variant={options.variant === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
          >
            {options.confirmLabel ?? 'Confirm'}
          </Button>
        </>
      }
    >
      <p className="text-sm text-slate-600 dark:text-slate-300">{options.message}</p>
    </Modal>
  );
}

/** Hook for imperative confirm — replaces window.confirm in pages. */
export function useConfirm() {
  const [state, setState] = useState<{
    open: boolean;
    options: ConfirmOptions;
    resolve: (value: boolean) => void;
  } | null>(null);

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setState({ open: true, options, resolve });
    });
  }, []);

  const handleConfirm = () => {
    state?.resolve(true);
    setState(null);
  };

  const handleCancel = () => {
    state?.resolve(false);
    setState(null);
  };

  const dialog = (
    <ConfirmDialog
      open={Boolean(state?.open)}
      options={state?.options ?? null}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );

  return { confirm, dialog };
}
